import { none, Option } from '@sapphire/result';

import { CACHE_TTL, isCacheStale, Nullable, Undefinable } from '@ps/utils';
import {
  AdvancedQualification,
  mapSurveyLocalization,
  Qualification,
  SurveyLocaleData,
} from '@ps/survey-utils';
import { CachedEntry, mostRecentCacheKey } from './cache.helper';

/**
 * Cache key for a question-library search. Sorting the ids and normalising the
 * phrase makes `age` + `[7, 3]` and `Age ` + `[3, 7]` share one cache entry.
 * `locale` is keyed too — the library is localised, so one locale's results
 * must not be read back under another.
 * @param search - free-text search phrase
 * @param categoryIds - selected category filter ids
 * @param locale - the country/language locale the search was run under
 * @returns the record key for this search
 */
export const surveyAdvancedQualificationCacheKey = (
  search: string,
  categoryIds: number[],
  locale: string,
): string =>
  `${locale}|${search.trim().toLowerCase()}|${[...categoryIds]
    .sort((first, second) => first - second)
    .join(',')}`;

/**
 * Test if a question search is cached and not stale. Unlike
 * {@link isChildSurveysCacheValid} an empty array is a valid cached answer —
 * "no questions match this phrase" must not re-fetch on every keystroke.
 * @param key - key produced by {@link surveyAdvancedQualificationCacheKey}
 * @param questionsRecord - cached questions (with their cachedAt) by search key
 * @returns true if the search is cached and not stale.
 */
export const isSurveyQuestionsCacheValid = (
  key: string,
  questionsRecord: Record<string, CachedEntry<AdvancedQualification[]>>,
): boolean =>
  key in questionsRecord &&
  !isCacheStale(questionsRecord[key].cachedAt, CACHE_TTL.DEFAULT);

/**
 * Cache key for a location conditions load (search-type qualifications —
 * CSA/CBSA/County/DMA). Unlike {@link surveyAdvancedQualificationCacheKey}, there's no
 * search text in the key at all: the full option list for a qualification is
 * loaded once (searchText is never sent to the server) and filtered
 * client-side from then on, so every keystroke reads the same cache entry
 * instead of creating a new one. This key DOES gate the effect against
 * refetching (see loadLocationConditions$'s cache gate). `locale` is included
 * because `dbQualificationCode` is stable across countries for a given tab —
 * without it, a result cached for one country's catalog would be read back
 * under a different country.
 * @param dbQualificationCode - the qualification being loaded
 * @param locale - the country/language locale the load was run under
 * @returns the record key for this qualification+locale
 */
export const locationConditionsCacheKey = (
  dbQualificationCode: number,
  locale: string,
): string => `${dbQualificationCode}|${locale}`;

/**
 * Resolves the cached qualifications catalog for a locale, or (when
 * `recentLocaleWhenNull`) the most recently cached locale's catalog when no
 * locale is given. Extracted so both a signal-backed facade and an RxJS
 * effect can share the same lookup off their own read of
 * `qualificationsRecord`.
 */
export const resolveQualifications = (
  qualificationsRecord: Nullable<Record<string, CachedEntry<Qualification[]>>>,
  locale: Undefinable<SurveyLocaleData>,
  recentLocaleWhenNull = false,
): Qualification[] =>
  Option.from(locale)
    .map((value) =>
      mapSurveyLocalization(value.countryCode, value.languageShortCode),
    )
    .orElse(() =>
      recentLocaleWhenNull
        ? Option.from(
            mostRecentCacheKey(
              Object.fromEntries(
                Object.entries(qualificationsRecord ?? {}).map(
                  ([key, entry]) => [key, entry.cachedAt],
                ),
              ),
            ),
          )
        : none,
    )
    .mapOr([], (key: string) => qualificationsRecord?.[key]?.data || []);
